import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import { PolicyAgreement } from "@/components/PolicyAgreement";

const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consentAccepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy to create an account.");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, pw, consentAccepted);
      toast.success("Welcome to Eira!");
      nav("/");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const beginGoogleRegistration = () => {
    if (!consentAccepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    window.sessionStorage.setItem("eira-account-consent", "accepted");
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="register-page">
      <div className="text-center mb-8">
        <div className="text-xs tracking-[0.3em] uppercase text-[#8C6D46] mb-3">Join Eira</div>
        <h1 className="font-serif text-4xl font-semibold">Create account</h1>
      </div>
      <form onSubmit={submit} className="bg-white border border-[#E2DCD2] rounded-xl p-6 space-y-4">
        <div>
          <label className="text-sm font-medium">Full Name</label>
          <input data-testid="reg-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]" />
        </div>
        <div>
          <label className="text-sm font-medium">Email</label>
          <input data-testid="reg-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]" />
        </div>
        <div>
          <label className="text-sm font-medium">Password</label>
          <input data-testid="reg-password" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 w-full bg-white border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]" />
        </div>
        <PolicyAgreement
          checked={consentAccepted}
          onChange={setConsentAccepted}
          testIdPrefix="register"
        />
        <button disabled={loading || !consentAccepted} type="submit" className="w-full bg-[#2D5A47] hover:bg-[#214435] text-white font-semibold py-3 rounded-lg disabled:opacity-50" data-testid="reg-submit">
          {loading ? "Creating…" : "Create Account"}
        </button>
        <div className="relative text-center text-xs text-[#64748B]">
          <span className="bg-white px-2 relative z-10">or</span>
          <div className="absolute inset-x-0 top-1/2 border-t border-[#E2DCD2]" />
        </div>
        <button
          type="button"
          onClick={beginGoogleRegistration}
          disabled={!consentAccepted}
          className="w-full bg-white border border-[#E2DCD2] hover:border-[#2D5A47] py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="google-register"
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" /><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" /><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" /><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" /></svg>
          Sign up with Google
        </button>
        <div className="text-center text-sm text-[#64748B]">
          Already have an account? <Link to="/login" className="text-[#C86D42] font-medium">Sign in</Link>
        </div>
      </form>
    </div>
  );
};

export default Register;
